import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, User, ShoppingBag, Menu, X, LogOut } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/utils";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Canapés", to: "/canapes" },
  { label: "Collections", to: "/canapes?view=collections" },
  { label: "Nouveautés", to: "/canapes?sort=newest" },
  { label: "À propos", to: "/a-propos" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const { count, setIsOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { search: query } });
        setResults(data.slice(0, 5));
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const goSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/canapes?search=${encodeURIComponent(query)}`);
      setSearchOpen(false);
      setQuery("");
    }
  };

  const onHome = location.pathname === "/";
  const solid = scrolled || !onHome;

  return (
    <>
      <div className="bg-obsidian text-center text-[11px] uppercase tracking-[0.22em] text-cream/90 py-2 px-4">
        Livraison gant blanc offerte dès 1500€ · Fabrication artisanale européenne
      </div>

      <motion.header
        data-testid="main-header"
        className={`sticky top-0 z-50 transition-all duration-500 ${
          solid ? "glass shadow-soft" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <button
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            <Menu size={24} className={solid ? "text-foreground" : "text-white"} />
          </button>

          <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
            <span className={`font-serif text-2xl font-semibold tracking-tight lg:text-3xl ${solid ? "text-foreground" : "text-white"}`}>
              shop<span className="text-gold">·</span>canapé
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={`group relative text-sm font-medium tracking-wide transition-colors ${solid ? "text-foreground hover:text-gold-dark" : "text-white/90 hover:text-white"}`}
              >
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 lg:gap-4">
            <button onClick={() => setSearchOpen(true)} data-testid="header-search-button" aria-label="Rechercher">
              <Search size={20} className={solid ? "text-foreground" : "text-white"} />
            </button>

            {user ? (
              <div className="group relative hidden lg:block">
                <button data-testid="header-account-button" className="flex items-center gap-1">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover ring-1 ring-gold" />
                  ) : (
                    <User size={20} className={solid ? "text-foreground" : "text-white"} />
                  )}
                </button>
                <div className="invisible absolute right-0 top-full w-52 translate-y-2 rounded-2xl bg-card p-3 opacity-0 shadow-luxe transition-all group-hover:visible group-hover:translate-y-1 group-hover:opacity-100">
                  <p className="truncate px-2 py-1 text-sm font-medium">{user.name}</p>
                  <p className="truncate px-2 pb-2 text-xs text-muted-foreground">{user.email}</p>
                  <button
                    onClick={logout}
                    data-testid="header-logout-button"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-destructive transition-colors hover:bg-secondary"
                  >
                    <LogOut size={15} /> Déconnexion
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => navigate("/connexion")} data-testid="header-login-button" aria-label="Compte">
                <User size={20} className={solid ? "text-foreground" : "text-white"} />
              </button>
            )}

            <button onClick={() => setIsOpen(true)} data-testid="header-cart-button" className="relative" aria-label="Panier">
              <ShoppingBag size={20} className={solid ? "text-foreground" : "text-white"} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-obsidian"
                    data-testid="cart-count-badge"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div className="fixed inset-0 z-[80] bg-obsidian/50 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)} />
            <motion.div
              className="fixed left-0 top-0 z-[90] h-full w-4/5 max-w-xs bg-cream p-6 shadow-luxe lg:hidden"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              data-testid="mobile-menu"
            >
              <div className="mb-8 flex items-center justify-between">
                <span className="font-serif text-2xl">shop<span className="text-gold">·</span>canapé</span>
                <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X size={22} /></button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV.map((n, i) => (
                  <motion.div key={n.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                    <Link to={n.to} className="block border-b border-border py-3 font-serif text-xl">{n.label}</Link>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-8">
                {user ? (
                  <button onClick={logout} className="flex items-center gap-2 text-destructive"><LogOut size={16} /> Déconnexion</button>
                ) : (
                  <button onClick={() => navigate("/connexion")} className="w-full rounded-full bg-obsidian py-3 text-sm font-semibold text-cream">Se connecter</button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search modal */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-start justify-center bg-obsidian/50 backdrop-blur-sm px-4 pt-24"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSearchOpen(false)}
          >
            <motion.div
              className="w-full max-w-xl rounded-3xl bg-cream p-5 shadow-luxe"
              initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={goSearch} className="flex items-center gap-3 rounded-full border border-border bg-card px-5 py-3">
                <Search size={20} className="text-muted-foreground" />
                <input
                  autoFocus
                  data-testid="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un canapé, un fauteuil…"
                  className="flex-1 bg-transparent text-sm outline-none"
                />
                <button type="button" onClick={() => setSearchOpen(false)}><X size={18} className="text-muted-foreground" /></button>
              </form>
              {results.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="search-results">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { navigate(`/produit/${p.id}`); setSearchOpen(false); setQuery(""); }}
                      className="flex w-full items-center gap-4 rounded-2xl p-2 text-left transition-colors hover:bg-secondary"
                    >
                      <img src={p.image} alt="" className="h-14 w-14 rounded-xl object-cover" />
                      <div className="flex-1">
                        <p className="font-serif text-lg leading-tight">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.category}</p>
                      </div>
                      <span className="font-semibold">{formatPrice(p.price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
